import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Palmtree, Users, BedDouble, Maximize, ArrowUpRight } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

export const VillasSection = () => {
  const { lang, t } = useLang();
  const [villas, setVillas] = useState([]);

  useEffect(() => {
    api.get("/apartments").then((r) => {
      setVillas(r.data.filter((a) => a.category_mk === "Вили" || a.category_en === "Villas"));
    }).catch(() => {});
  }, []);

  return (
    <section id="villas" data-testid="villas-section" className="bg-coal text-sand py-24 md:py-36">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={villas.length ? "mb-14 md:mb-20" : "text-center"}
        >
          <p className="text-ember text-xs font-bold tracking-[0.35em] uppercase mb-4">{villas.length ? t("nav.villas") : t("villas.kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-6">{t("villas.title")}</h2>
          {!villas.length && (
            <p className="text-sand/60 text-sm md:text-base leading-relaxed max-w-md mx-auto mb-10">{t("villas.sub")}</p>
          )}
        </motion.div>

        {villas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {villas.map((villa, i) => (
              <motion.div
                key={villa.slug}
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
              >
                <Link
                  to={`/apartments/${villa.slug}`}
                  data-testid={`villa-card-${villa.slug}`}
                  className="group block border border-sand/15 bg-coal2 hover:border-ember/60 transition-colors duration-500"
                >
                  <div className="spotlight-frame aspect-[4/3]">
                    <img src={villa.images[0]} alt={lang === "mk" ? villa.name_mk : villa.name_en} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-4 left-4 bg-coal/80 backdrop-blur px-3 py-1.5 text-sand text-[11px] font-semibold tracking-widest uppercase">
                      {lang === "mk" ? villa.location_mk : villa.location_en}
                    </div>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-2xl md:text-3xl tracking-tight group-hover:text-ember transition-colors duration-300">
                          {lang === "mk" ? villa.name_mk : villa.name_en}
                        </h3>
                        <p className="text-sand/55 text-sm mt-1">{lang === "mk" ? villa.tagline_mk : villa.tagline_en}</p>
                      </div>
                      <div className="w-10 h-10 shrink-0 border border-sand/20 flex items-center justify-center group-hover:bg-ember group-hover:border-ember group-hover:text-coal transition-colors duration-300">
                        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="mt-6 pt-5 border-t border-sand/10 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-5 text-sand/60">
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="w-4 h-4" strokeWidth={1.5} /> {villa.max_guests} {t("grid.guests")}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <BedDouble className="w-4 h-4" strokeWidth={1.5} /> {villa.bedrooms === 0 ? t("grid.studio") : `${villa.bedrooms} ${t("grid.bedrooms")}`}
                        </span>
                        <span className="hidden sm:inline-flex items-center gap-1.5">
                          <Maximize className="w-4 h-4" strokeWidth={1.5} /> {villa.size_m2} m²
                        </span>
                      </div>
                      <p className="font-serif text-xl whitespace-nowrap">
                        <span className="text-xs font-sans text-sand/45 mr-1">{t("grid.from")}</span>€{villa.price_per_night}
                        <span className="text-xs font-sans text-sand/45">{t("grid.perNight")}</span>
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex flex-col items-center gap-2">
                <a
                  href="https://wa.me/38978321872"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="villas-whatsapp-button"
                  className="inline-flex items-center gap-3 px-8 py-4 text-xs font-bold tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-[1.03]"
                  style={{ backgroundColor: "#25D366", color: "#0b1f13" }}
                >
                  <img src="https://cdn.simpleicons.org/whatsapp/0b1f13" alt="WhatsApp" className="w-5 h-5" /> WhatsApp
                </a>
              </div>
              <div className="flex flex-col items-center gap-2">
                <a
                  href="viber://chat?number=%2B38978321872"
                  data-testid="villas-viber-button"
                  className="inline-flex items-center gap-3 px-8 py-4 text-xs font-bold tracking-[0.2em] uppercase text-white transition-transform duration-300 hover:scale-[1.03]"
                  style={{ backgroundColor: "#7360F2" }}
                >
                  <img src="https://cdn.simpleicons.org/viber/ffffff" alt="Viber" className="w-5 h-5" /> Viber
                </a>
              </div>
            </div>
            <Palmtree className="w-6 h-6 text-ember/40 mx-auto mt-14" strokeWidth={1.5} />
          </div>
        )}
      </div>
    </section>
  );
};
